// frontend\src\pages\ScanCatalog.js
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/api";
import { SCANS_API } from "../constants";
import { toast } from "react-toastify";
import Pagination from "../components/Pagination";
import Loader from "../components/Loader";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { useAuth } from "../hooks/useAuth";
import { useFileExport } from "../hooks/useFileExport";
import ConfirmModal from "../components/ConfirmModal";
import {
  BiFilter,
  BiChevronDown,
  BiChevronUp,
  BiSearch,
  BiPrinter,
  BiPlusCircle,
  BiEdit,
  BiTrash,
  BiLinkExternal,
  BiReset,
} from "react-icons/bi";

const ScanCatalog = () => {
  const [scans, setScans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    film: "",
    development_type: "",
    camera: "",
    lenses: "",
    from_date: "",
    to_date: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filmOptions, setFilmOptions] = useState([]);
  const [cameraOptions, setCameraOptions] = useState([]);
  const [lensOptions, setLensOptions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const perPageOptions = [20, 50, 100, "all"];
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isExporting, exportProgress, exportFile } = useFileExport();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
      return;
    }
  }, [isAuthenticated, authLoading, navigate]);

  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      let url = `${SCANS_API}`;
      const params = [];

      if (appliedFilters.searchTerm) {
        params.push(`search=${encodeURIComponent(appliedFilters.searchTerm)}`);
      }
      if (appliedFilters.film) {
        params.push(`film=${encodeURIComponent(appliedFilters.film)}`);
      }
      if (appliedFilters.development_type) {
        params.push(`development_type=${appliedFilters.development_type}`);
      }
      if (appliedFilters.camera) {
        params.push(`camera=${encodeURIComponent(appliedFilters.camera)}`);
      }
      if (appliedFilters.lenses) {
        params.push(`lenses=${encodeURIComponent(appliedFilters.lenses)}`);
      }
      if (appliedFilters.from_date) {
        params.push(`from_date=${appliedFilters.from_date}`);
      }
      if (appliedFilters.to_date) {
        params.push(`to_date=${appliedFilters.to_date}`);
      }

      if (perPage !== "all") {
        params.push(`page=${currentPage}`);
        params.push(`per_page=${perPage}`);
      } else {
        params.push(`per_page=all`);
      }

      if (params.length > 0) {
        url += "?" + params.join("&");
      }

      const response = await api.get(url, {});

      const results = response.data.results || response.data;
      const count =
        response.data.count !== undefined
          ? response.data.count
          : results.length;

      setScans(results);
      setTotalCount(count);
      setTotalPages(perPage === "all" ? 1 : Math.ceil(count / perPage));
    } catch (error) {
      console.error("Ошибка загрузки данных:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error("Ошибка загрузки данных!");
      }
      setScans([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, appliedFilters, isAuthenticated, navigate, perPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadOptions = async () => {
      try {
        const [filmRes, cameraRes, lensesRes] = await Promise.all([
          api.get(`${SCANS_API}films/`),
          api.get(`${SCANS_API}cameras/`),
          api.get(`${SCANS_API}lenses/`),
        ]);
        setFilmOptions(filmRes.data);
        setCameraOptions(cameraRes.data);
        setLensOptions(lensesRes.data);
      } catch (error) {
        console.error("Ошибка загрузки опций:", error);
        toast.error("Ошибка загрузки опций!");
      }
    };

    loadOptions();
  }, [isAuthenticated]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`${SCANS_API}${id}/`);
      setScans((prevScans) => prevScans.filter((scan) => scan.id !== id));
      setShowConfirm(null);
      toast.success("Запись успешно удалена!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error("Ошибка удаления:", error);
      toast.error("Ошибка удаления записи!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = () => {
    setAppliedFilters({
      searchTerm,
      ...filters,
    });
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      film: "",
      development_type: "",
      camera: "",
      lenses: "",
      from_date: "",
      to_date: "",
    });
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const handleExportLabels = () => {
    if (scans.length === 0) {
      toast.info("Нет данных для экспорта");
      return;
    }
    
    exportFile(
      (options) => api.get(`${SCANS_API}export-labels/file/`, options),
      "film_labels.pdf",
      "Этикетки успешно экспортированы!"
    );
  };

  const handlePerPageChange = (e) => {
    const value = e.target.value;
    setPerPage(value === "all" ? "all" : parseInt(value, 10));
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const hasActiveFilters =
    searchTerm ||
    filters.film ||
    filters.development_type ||
    filters.camera ||
    filters.lenses ||
    filters.from_date ||
    filters.to_date;

  return (
    <div className="container-fluid mt-4">
      {/* === ДЕСКТОП: Полные кнопки === */}
      <div className="d-none d-lg-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Мой каталог</h1>
        <div>
          <button
            onClick={handleExportLabels}
            className="btn btn-outline-primary me-2"
            disabled={scans.length === 0 || isExporting}
          >
            {isExporting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Генерация ({exportProgress}%)
              </>
            ) : (
              <>
                <BiPrinter className="me-2" />
                Экспорт этикеток
              </>
            )}
          </button>
          <Link to="/catalog/new" className="btn btn-primary">
            <BiPlusCircle className="me-2" />
            Новая запись
          </Link>
        </div>
      </div>

      {/* === МОБИЛЬНЫЙ: Компактные кнопки под заголовком === */}
      <div className="d-lg-none d-flex flex-column gap-3 mb-4">
        <h1 className="h4 mb-0">Мой каталог</h1>
        <div className="d-flex gap-2">
          <button
            onClick={handleExportLabels}
            className="btn btn-outline-primary btn-sm flex-fill"
            disabled={scans.length === 0 || isExporting}
          >
            {isExporting ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              "Экспорт"
            )}
          </button>
          <Link to="/catalog/new" className="btn btn-primary btn-sm flex-fill">
            Новая
          </Link>
        </div>
      </div>

      {isExporting && (
        <div className="progress mb-3">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${exportProgress}%` }}
            aria-valuenow={exportProgress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {exportProgress}%
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-outline-primary d-flex align-items-center"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          type="button"
        >
          <BiFilter className="me-2" />
          Фильтры
          {isFiltersExpanded ? (
            <BiChevronUp className="ms-2" />
          ) : (
            <BiChevronDown className="ms-2" />
          )}
        </button>

        {!isFiltersExpanded && hasActiveFilters && (
          <div className="d-flex flex-wrap gap-2">
            {searchTerm && (
              <span className="badge bg-primary">Поиск: {searchTerm}</span>
            )}
            {filters.film && (
              <span className="badge bg-secondary">Плёнка: {filters.film}</span>
            )}
            {filters.development_type && (
              <span className="badge bg-info text-dark">
                Проявка:{" "}
                {filters.development_type === "home" ? "Дома" : "Лаборатория"}
              </span>
            )}
            {filters.camera && (
              <span className="badge bg-warning text-dark">
                Камера: {filters.camera}
              </span>
            )}
            {filters.lenses && (
              <span className="badge bg-success">
                Объективы: {filters.lenses}
              </span>
            )}
            {(filters.from_date || filters.to_date) && (
              <span className="badge bg-danger">
                Дата: {filters.from_date || "∞"} - {filters.to_date || "∞"}
              </span>
            )}
          </div>
        )}
      </div>

      <div className={`card mb-4 ${isFiltersExpanded ? "" : "d-none"}`}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Поиск</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Поиск по локации, комментарию..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>

            <div className="col-md-2">
              <label className="form-label">Плёнка</label>
              <select
                className="form-select"
                name="film"
                value={filters.film}
                onChange={handleFilterChange}
              >
                <option value="">Все плёнки</option>
                <option disabled>---</option>
                {filmOptions.map((film) => (
                  <option key={film} value={film}>
                    {film}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Тип проявки</label>
              <select
                className="form-select"
                name="development_type"
                value={filters.development_type}
                onChange={handleFilterChange}
              >
                <option value="">Все</option>
                <option disabled>---</option>
                <option value="home">Дома</option>
                <option value="lab">Лаборатория</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Камера</label>
              <select
                className="form-select"
                name="camera"
                value={filters.camera}
                onChange={handleFilterChange}
              >
                <option value="">Все камеры</option>
                <option disabled>---</option>
                {cameraOptions.map((camera) => (
                  <option key={camera} value={camera}>
                    {camera}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Объективы</label>
              <select
                className="form-select"
                name="lenses"
                value={filters.lenses}
                onChange={handleFilterChange}
              >
                <option value="">Все объективы</option>
                <option disabled>---</option>
                {lensOptions.map((lens) => (
                  <option key={lens} value={lens}>
                    {lens}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Дата от</label>
                  <input
                    type="date"
                    className="form-control"
                    name="from_date"
                    value={filters.from_date}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Дата до</label>
                  <input
                    type="date"
                    className="form-control"
                    name="to_date"
                    value={filters.to_date}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>
            </div>

            <div className="col-md-4 d-flex justify-content-end align-items-end gap-2">
              <div className="d-flex gap-2">
                <button className="btn btn-primary" onClick={applyFilters}>
                  <BiSearch className="me-2" />
                  Применить
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={resetFilters}
                  title="Сбросить фильтры"
                >
                  <BiReset />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="perPageSelect" className="form-label">
          Записей на страницу:
        </label>
        <select
          id="perPageSelect"
          className="form-select w-auto d-inline-block ms-2"
          value={perPage}
          onChange={handlePerPageChange}
        >
          {perPageOptions.map((option) => (
            <option key={option} value={option}>
              {option === "all" ? "Все" : option}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <Loader />}

      {scans.length > 0 && (
        <div className="alert alert-info py-2 mb-3">
          Найдено: <strong>{totalCount}</strong>{" "}
          {totalCount === 1
            ? "запись"
            : totalCount >= 2 && totalCount <= 4
            ? "записи"
            : "записей"}
          {perPage !== "all" && totalPages > 1 && (
            <span className="ms-2">
              (Показано: <strong>{scans.length}</strong>)
            </span>
          )}
        </div>
      )}

      {/* === ДЕСКТОП: ТАБЛИЦА === */}
      <div className="d-none d-lg-block">
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th style={{ width: "50px" }}>№</th>
                <th style={{ width: "100px" }}>Дата</th>
                <th>Локация</th>
                <th>Камера</th>
                <th>Объективы</th>
                <th>Плёнка</th>
                <th>Тип проявки</th>
                <th>Проявитель/Лаборатория</th>
                <th>Время (мин)</th>
                <th>Комментарии</th>
                <th>Ссылка</th>
                <th style={{ width: "150px" }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {scans.length > 0 ? (
                scans.map((scan) => (
                  <tr key={scan.id}>
                    <td className="text-center fw-bold">{scan.catalog_number}</td>
                    <td>
                      {scan.scan_date
                        ? new Date(scan.scan_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>{scan.location || "-"}</td>
                    <td>{scan.camera || "-"}</td>
                    <td>
                      {scan.lenses ? (
                        <div className="d-flex flex-wrap gap-1">
                          {scan.lenses.split(",").map((lens, index) => (
                            <span key={index} className="badge bg-secondary">
                              {lens.trim()}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{scan.film || "-"}</td>
                    <td>
                      {scan.development_type === "home" ? "Дома" : "Лаборатория"}
                    </td>
                    <td>
                      {scan.development_type === "home"
                        ? `${scan.developer || ""} ${scan.dilution ? `(${scan.dilution})` : ""}`.trim() || "-"
                        : scan.lab_name || "-"}
                    </td>
                    <td>
                      {scan.development_type === "home" && scan.development_time
                        ? scan.development_time
                        : "-"}
                    </td>
                    <td className="small">{scan.comments || "-"}</td>
                    <td>
                      {scan.link ? (
                        <a
                          href={scan.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          <BiLinkExternal />
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Link
                          to={`/catalog/edit/${scan.id}`}
                          className="btn btn-sm btn-outline-primary"
                          title="Редактировать"
                        >
                          <BiEdit />
                        </Link>

                        <Link
                          to="/catalog/new"
                          state={{ copyFrom: scan }}
                          className="btn btn-sm btn-outline-success"
                          title="Копировать"
                        >
                          <i className="bi bi-files"></i>
                        </Link>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setShowConfirm(scan.id)}
                          title="Удалить"
                        >
                          <BiTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    {!isLoading && (
                      <div className="alert alert-info mb-0">
                        {hasActiveFilters ? (
                          "Ничего не найдено. Попробуйте изменить параметры поиска."
                        ) : (
                          <>
                            В каталоге пока нет записей.{" "}
                            <Link to="/catalog/new" className="alert-link">
                              Создайте первую запись
                            </Link>
                            .
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* === МОБИЛЬНЫЕ: КАРТОЧКИ === */}
      <div className="d-lg-none">
        {scans.length > 0 ? (
          scans.map((scan) => (
            <div key={scan.id} className="card mb-3">
              <div className="card-body">
                <h6 className="card-title mb-2">
                  <span className="fw-bold me-2">№{scan.catalog_number}</span>
                  {scan.scan_date
                    ? new Date(scan.scan_date).toLocaleDateString()
                    : "-"}
                </h6>
                <div className="small text-muted mb-2">
                  <strong>Локация:</strong> {scan.location || "-"}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Камера:</strong> {scan.camera || "-"}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Объективы:</strong>{" "}
                  {scan.lenses ? (
                    scan.lenses.split(",").map((lens, index) => (
                      <span key={index} className="badge bg-secondary me-1">
                        {lens.trim()}
                      </span>
                    ))
                  ) : (
                    "-"
                  )}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Плёнка:</strong> {scan.film || "-"}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Проявка:</strong>{" "}
                  {scan.development_type === "home" ? "Дома" : "Лаборатория"}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Проявитель/Лаборатория:</strong>{" "}
                  {scan.development_type === "home"
                    ? `${scan.developer || ""} ${scan.dilution ? `(${scan.dilution})` : ""}`.trim() || "-"
                    : scan.lab_name || "-"}
                </div>
                <div className="small text-muted mb-2">
                  <strong>Время:</strong>{" "}
                  {scan.development_type === "home" && scan.development_time
                    ? scan.development_time
                    : "-"}
                </div>
                {scan.comments && (
                  <p className="small text-muted fst-italic mb-2">
                    {scan.comments}
                  </p>
                )}
                <div className="small text-muted mb-2">
                  <strong>Ссылка:</strong>{" "}
                  {scan.link ? (
                    <a
                      href={scan.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                    >
                      <BiLinkExternal className="me-1" />
                      Открыть
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Link
                    to={`/catalog/edit/${scan.id}`}
                    className="btn btn-sm btn-outline-primary"
                    title="Редактировать"
                  >
                    <BiEdit />
                  </Link>

                  <Link
                    to="/catalog/new"
                    state={{ copyFrom: scan }}
                    className="btn btn-sm btn-outline-success"
                    title="Копировать"
                  >
                    <i className="bi bi-files"></i>
                  </Link>

                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setShowConfirm(scan.id)}
                    title="Удалить"
                  >
                    <BiTrash />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="alert alert-info">
            {!isLoading && (
              <>
                {hasActiveFilters ? (
                  "Ничего не найдено. Попробуйте изменить параметры поиска."
                ) : (
                  <>
                    В каталоге пока нет записей.{" "}
                    <Link to="/catalog/new" className="alert-link">
                      Создайте первую запись
                    </Link>
                    .
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <ScrollToTopButton />
      {totalPages > 1 && perPage !== "all" && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      <ConfirmModal
        isOpen={!!showConfirm}
        onClose={() => setShowConfirm(null)}
        onConfirm={() => handleDelete(showConfirm)}
        title="Подтверждение удаления"
        message="Вы уверены, что хотите удалить эту запись? Это действие нельзя отменить."
        confirmText="Удалить"
        cancelText="Отмена"
        type="delete"
      />
    </div>
  );
};

export default ScanCatalog;